import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  FileDownload,
  ArrowBack,
  ArrowForward,
  Business,
  PictureAsPdf,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import api from '../../api/axios';
import { insuranceCompanyService } from '../../services/insuranceCompanyService';

interface ReportData {
  id: number;
  assignedDate: string;
  requestId: string;
  vehicle: string;
  year: string | number;
  plate: string;
  phone: string;
  from: string;
  to: string;
  serviceType: string;
  reason: string;
  policyNumber: string;
  insuranceCompany: string;
  insuranceCompanyId: number | null;
}

const ServiceRequestReport: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch insurance companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await insuranceCompanyService.getAll();
        setCompanies(Array.isArray(data) ? data : []);
      } catch (err: any) {
        console.error('Error fetching companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch report data only when a company is selected
  useEffect(() => {
    if (selectedCompany) {
      fetchReportData();
    } else {
      // Clear data when no company is selected
      setReportData([]);
      setError('');
    }
  }, [currentDate.month, currentDate.year, selectedCompany]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: any = {
        month: currentDate.month,
        year: currentDate.year,
      };

      if (selectedCompany) {
        params.insuranceCompanyId = selectedCompany;
      }

      const response = await api.get('/requests/report', { params });
      setReportData(response.data?.data || []);
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to fetch report data');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate((prev) => {
      let newMonth = prev.month - 1;
      let newYear = prev.year;
      
      if (newMonth < 1) {
        newMonth = 12;
        newYear -= 1;
      }
      
      return { month: newMonth, year: newYear };
    });
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => {
      let newMonth = prev.month + 1;
      let newYear = prev.year;
      
      if (newMonth > 12) {
        newMonth = 1;
        newYear += 1;
      }
      
      return { month: newMonth, year: newYear };
    });
  };

  const handleDownloadPDF = async () => {
    if (filteredData.length === 0) return;

    // Use landscape orientation for better table fit
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Add logo
    await addLogoToPDF(doc);

    // Report title
    const monthName = getMonthName();
    const companyName = selectedCompany 
      ? companies.find(c => (c.id || c.attributes?.id || c.data?.id)?.toString() === selectedCompany)?.attributes?.name || companies.find(c => c.id?.toString() === selectedCompany)?.name || 'All Companies'
      : 'All Companies';

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Service Request Report', 20, 25);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${monthName}`, 20, 32);
    doc.text(`Company: ${companyName}`, 20, 38);
    doc.text(`Total Requests: ${filteredData.length}`, 20, 44);

    // Prepare table data
    const tableData = filteredData.map((row) => [
      row.assignedDate,
      row.requestId,
      row.vehicle,
      row.year?.toString() || 'N/A',
      row.plate,
      row.phone,
      row.from,
      row.to,
      row.serviceType,
      row.reason,
      row.policyNumber,
    ]);

    // Add table
    autoTable(doc, {
      startY: 50,
      head: [['Assign Date', 'Request ID', 'Vehicle', 'Year', 'Plate', 'Phone', 'From', 'To', 'Service Type', 'Reason', 'Policy Number']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Assign Date
        1: { cellWidth: 20 }, // Request ID
        2: { cellWidth: 24 }, // Vehicle
        3: { cellWidth: 12 }, // Year
        4: { cellWidth: 16 }, // Plate
        5: { cellWidth: 20 }, // Phone
        6: { cellWidth: 28 }, // From
        7: { cellWidth: 28 }, // To
        8: { cellWidth: 24 }, // Service Type
        9: { cellWidth: 40 }, // Reason
        10: { cellWidth: 23 }, // Policy Number
      },
      margin: { left: 15, right: 15 },
    });

    // Generate filename
    const filename = `Service-Request-Report-${monthName.replace(/\s+/g, '-')}-${companyName.replace(/\s+/g, '-')}.pdf`;
    doc.save(filename);
  };

  const getMonthName = () => {
    return new Date(currentDate.year, currentDate.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const getFilteredData = () => {
    // If a company is selected, filter by it (though backend should already filter)
    if (!selectedCompany) return reportData;
    
    return reportData.filter((row) => {
      const companyId = row.insuranceCompanyId?.toString();
      return companyId === selectedCompany;
    });
  };

  const filteredData = getFilteredData();

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Service Request Report
        </Typography>
        <Button
          variant="contained"
          startIcon={<PictureAsPdf />}
          onClick={handleDownloadPDF}
          disabled={filteredData.length === 0 || loading}
        >
          Download PDF
        </Button>
      </Stack>

      {/* Filters and Month Navigation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            {/* Month Navigation */}
            <Stack direction="row" spacing={2} alignItems="center" flex={1}>
              <IconButton onClick={handlePreviousMonth} disabled={loading}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                {getMonthName()}
              </Typography>
              <IconButton onClick={handleNextMonth} disabled={loading}>
                <ArrowForward />
              </IconButton>
            </Stack>

            {/* Company Filter */}
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Insurance Company</InputLabel>
              <Select
                value={selectedCompany}
                label="Insurance Company"
                onChange={(e) => setSelectedCompany(e.target.value)}
                startAdornment={selectedCompany ? <Business sx={{ mr: 1, color: 'action.active' }} /> : null}
              >
                <MenuItem value="">All Companies</MenuItem>
                {companies.map((company) => {
                  const id = company.id || company.attributes?.id || company.data?.id;
                  const name = company.name || company.attributes?.name || company.data?.attributes?.name;
                  return (
                    <MenuItem key={id} value={id?.toString()}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Report Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Alert severity="info">
              {!selectedCompany 
                ? 'Please select an insurance company to view the report.'
                : `No service requests found for ${getMonthName()} for the selected company.`}
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Total: {filteredData.length} request(s)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Assign Date</strong></TableCell>
                      <TableCell><strong>Request ID</strong></TableCell>
                      <TableCell><strong>Vehicle</strong></TableCell>
                      <TableCell><strong>Year</strong></TableCell>
                      <TableCell><strong>Plate</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>From</strong></TableCell>
                      <TableCell><strong>To</strong></TableCell>
                      <TableCell><strong>Service Type</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell><strong>Policy Number</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.assignedDate}</TableCell>
                        <TableCell>
                          <Chip label={row.requestId} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{row.vehicle}</TableCell>
                        <TableCell>{row.year}</TableCell>
                        <TableCell>{row.plate}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>{row.from}</TableCell>
                        <TableCell>{row.to}</TableCell>
                        <TableCell>{row.serviceType}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap title={row.reason}>
                            {row.reason}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.policyNumber}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServiceRequestReport;
